import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form, Select } from "antd";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import axios, { AxiosError, AxiosResponse } from "axios";
import { IAllocation } from "..";
import getValidationErrors from "../../../../utils/getValidationErrors";
import FormItem from "../../../../components/FormItem";
import { useToast } from "../../../../hooks/toast";
import api from "../../../../services/api";
import { removeNullKeys } from "../../../../utils/formatters";
import { IUser } from "../../../Users";
import UserRoles from "../../../../enums/UserRoles";

interface IUserModalProps {
  visible: boolean;
  handleCloseModal: (opex?: IAllocation[]) => void;
  opex?: IAllocation;
  state: IAllocation[];
}

interface IUserFormData {
  manager_id: string;
}

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

const UserModal: React.FC<IUserModalProps> = ({
  visible,
  handleCloseModal,
  opex,
  state,
}) => {
  const { t } = useTranslation(["rectability"]);
  const [loading, setLoading] = useState(false);
  const [lastOPex, setLastOpex] = useState<IAllocation>();
  const [validationErrors, setValidationErrors] = useState({});
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [managers, setManagers] = useState<IUser[]>([]);

  const { addToast } = useToast();
  const [form] = Form.useForm();

  const isEditing = useMemo(() => !!opex, [opex]);

  useEffect(() => {
    setLoadingManagers(true);
    api
      .get<IUser[]>("users")
      .then((response) => {
        const users = response.data.filter(
          (userOnly) => !userOnly?.factory && userOnly?.model === "FACTORY"
        );
        setManagers(users);
      })
      .catch(() => {
        addToast({
          type: "error",
          title: "Erro na busca",
          description:
            "Occoreu um erro ao buscar os gestores, tente novamente.",
        });
      })
      .finally(() => setLoadingManagers(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    form.setFieldsValue({
      ...opex,
    });
  }, [form, opex]);

  const handleClose = useCallback(
    (usr?: IAllocation[]) => {
      form.resetFields();
      handleCloseModal(usr);
    },
    [form, handleCloseModal]
  );

  const formatDate = useCallback((date: any) => {
    if (!date) {
      return undefined;
    }
    return date.format("YYYY-MM-DD");
  }, []);

  const generateErrorToast = useCallback(
    (response: AxiosResponse | undefined) => {
      let description;

      switch (response?.status) {
        case 403:
          description = "O usuário logado não é gestor";
          break;
        case 401:
          description = "Opex precisa ser adicionado com intervalo de 3 meses";
          break;
        case 404:
          description = "Opex Planejado não foi encontrado";
          break;
        case 409:
          description = "Usuario já foi adicionado";
          break;
        case 422:
          description = "Usuario possue algum campo importante como nulo";
          break;
        default:
          description = isEditing
            ? "Ocorreu um erro ao alterar os dados da rentabilidade de fábrica, tente novamente"
            : "Ocorreu um erro ao criar os dados da rentabilidade de fábrica, tente novamente";
      }

      addToast({
        type: "error",
        title: isEditing
          ? t("factory.requestEq.errorCreate")
          : t("factory.requestEq.errorCreate"),
        description,
      });
    },
    [addToast, isEditing, t]
  );

  const handleSubmit = useCallback(
    async (data: IUserFormData) => {
      removeNullKeys(data);

      try {
        const schema = Yup.object().shape({
          manager_id: Yup.string()
            .nullable()
            .when("role", {
              is: (role) =>
                role !== UserRoles.Manager && role !== UserRoles.Administrative,
              then: Yup.string().required("Manager obrigatório"),
            }),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        setLoading(true);

        const { manager_id } = data;

        let response: AxiosResponse;

        if (isEditing) {
          response = await api.patch(`/factory/${opex?.id}`);
        } else {
          response = await api.post(`/factory/${manager_id}`);
        }

        handleClose([response.data]);
      } catch (err) {
        console.log(err);
        addToast({
          type: "error",
          title: ((err as unknown) as any)?.status,
          description: ((err as unknown) as any)?.message,
        });
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          setValidationErrors(errors);
          return;
        }

        if (axios.isAxiosError(err)) {
          const serverError = err as AxiosError;
          generateErrorToast(serverError.response);
          return;
        }

        console.log(err);
      } finally {
        setLoading(false);
      }
    },
    [
      addToast,
      formatDate,
      generateErrorToast,
      handleClose,
      isEditing,
      opex,
      t,
      visible,
      lastOPex,
    ]
  );

  const managerOptions = useMemo(() => {
    if (!managers) {
      return undefined;
    }

    return managers.map((manager) => ({
      label: manager.name,
      value: manager.id,
    }));
  }, [managers]);

  return (
    <Modal
      title={isEditing ? t("factory.modal.edit") : t("factory.modal.create")}
      visible={visible}
      destroyOnClose
      onOk={() => form.submit()}
      onCancel={() => handleClose()}
      okButtonProps={{ htmlType: "submit", loading }}
      getContainer={false}
      forceRender
    >
      <Form
        {...layout}
        form={form}
        name="control-hooks"
        onFinish={handleSubmit}
      >
        {!isEditing ? (
          <FormItem
            name="manager_id"
            required
            label="Usuário"
            errors={validationErrors}
          >
            <Select
              allowClear
              options={managerOptions}
              loading={loadingManagers}
            />
          </FormItem>
        ) : (
          <FormItem />
        )}
      </Form>
    </Modal>
  );
};

export default UserModal;
